import { BaseEffect, EasingFunction, Easing, CanvasDimensions, EffectResult } from './BaseEffect';

/**
 * PanEffect animates the translation of the video or image.
 *
 * The `from` and `to` parameters are translation offsets, specified as normalized values (0-1).
 * They represent the fraction of the width and height by which the image is shifted during the effect.
 *
 * For example, from: {x: 0, y: 0} to: {x: 1, y: 0} will shift the image by the full width of the canvas to the right.
 *
 * These values do NOT specify an anchor point (such as the top-left or center of the visible area), but rather how much to move the image.
 *
 * @param from - Starting translation offset (normalized: 0-1)
 * @param to - Ending translation offset (normalized: 0-1)
 * @param duration - Duration in seconds
 * @param at - Start time in seconds
 * @param easing - Easing function
 * @param isNormalized - If false, from/to are in pixels and will be normalized internally
 * @param videoWidth - Required if isNormalized is false
 * @param videoHeight - Required if isNormalized is false
 */
export class PanEffect extends BaseEffect {
    from: { x: number, y: number }; // normalized (0-1)
    to: { x: number, y: number };   // normalized (0-1)

    constructor(
        from: { x: number, y: number },
        to: { x: number, y: number },
        duration: number,
        at?: number,
        easing: EasingFunction = Easing.easeInOutCubic,
        isNormalized: boolean = true,
        videoWidth?: number,
        videoHeight?: number
    ) {
        super('pan', duration, at, easing);
        if (!isNormalized) {
            if (!videoWidth || !videoHeight) {
                throw new Error('videoWidth and videoHeight are required when isNormalized is false');
            }
            this.from = { x: from.x / videoWidth, y: from.y / videoHeight };
            this.to = { x: to.x / videoWidth, y: to.y / videoHeight };
        } else {
            this.from = from;
            this.to = to;
        }
    }

    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult {
        const currentX = this.from.x + (this.to.x - this.from.x) * progress;
        const currentY = this.from.y + (this.to.y - this.from.y) * progress;
        
        // Convert normalized values to pixels using resolution dimensions
        const pixelX = currentX * dimensions.resolutionWidth;
        const pixelY = currentY * dimensions.resolutionHeight;
        
        ctx.translate(pixelX, pixelY);
        return prev;
    }
} 